import React, { useEffect, useRef, useState } from "react";
import { SearchOutlined } from "@ant-design/icons";
import { Button, Input, Space, Table, type InputRef, type TableColumnsType, type TableColumnType } from "antd";
import type { FilterDropdownProps } from "antd/es/table/interface";
import Highlighter from "react-highlight-words";
import axios from "axios";
import "./location_code_rack_goods_receive_return.css";

interface LocationDataType {
    id: number;
    code_rack: string;
    name_rack: string;
    warehouse_code?: string;
}

interface LocationCodeRackProps {
    warehouse?: string;
    onSelectLocation?: (codeRack: string) => void;
}

type DataIndex = keyof LocationDataType;

const LocationCodeRackGoodsReceiveReturn: React.FC<LocationCodeRackProps> = ({ warehouse, onSelectLocation }) => {
    const [data, setData] = useState<LocationDataType[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [searchText, setSearchText] = useState("");
    const [searchedColumn, setSearchedColumn] = useState("");
    const searchInput = useRef<InputRef>(null);
    const transformStyle = {
        transform: "scale(0.80)",
        transformOrigin: "top left",
        width: "125%",
    };

    useEffect(() => {
        const fetchRackLocations = async () => {
            if (!warehouse) return;
            setLoading(true);
            try {
                const response = await axios.get(
                    `${process.env.REACT_APP_BACKEND_URL}/api/spareparts-rack-location`,
                    { params: { warehouse_code: warehouse } }
                );
                setData(Array.isArray(response.data) ? response.data : []);
            } catch (error) {
                console.error("Failed to fetch rack location data:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchRackLocations();
    }, [warehouse]);

    const handleSearch = (
        selectedKeys: string[],
        confirm: FilterDropdownProps["confirm"],
        dataIndex: DataIndex,
    ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText("");
    };

    const getColumnSearchProps = (dataIndex: DataIndex): TableColumnType<LocationDataType> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex.replace(/_/g, " ")}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: "block" }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}>
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}>
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}>
                        close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />,
        onFilter: (value, record) =>
            (record[dataIndex] ?? "")
                .toString()
                .toLowerCase()
                .includes((value as string).toLowerCase()),
        filterDropdownProps: {
            onOpenChange(open) {
                if (open) {
                    setTimeout(() => searchInput.current?.select(), 100);
                }
            },
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ""}
                />
            ) : (
                text
            ),
    });

    const columns: TableColumnsType<LocationDataType> = [
        {
            title: "Location Code",
            dataIndex: "code_rack",
            key: "code_rack",
            ...getColumnSearchProps("code_rack"),
            sorter: (a, b) => (a.code_rack || "").localeCompare(b.code_rack || ""),
            sortDirections: ["descend", "ascend"],
        },
        {
            title: "Location Name",
            dataIndex: "name_rack",
            key: "name_rack",
            ...getColumnSearchProps("name_rack"),
            sorter: (a, b) => (a.name_rack || "").localeCompare(b.name_rack || ""),
            sortDirections: ["descend", "ascend"],
        },
    ];

    return (

        <div id="iniz" style={transformStyle}>
            <div className="gjs-grid-row" id="iddkp">
                <div className="gjs-grid-column" id="i7qxk">
                    <Table<LocationDataType>
                        rowKey="id"
                        columns={columns}
                        dataSource={data}
                        loading={loading}
                        onRow={(record) => ({
                            onDoubleClick: () => {
                                if (onSelectLocation) {
                                    onSelectLocation(record.code_rack);
                                }
                            },
                        })}
                    />
                </div>
            </div>
        </div>
    );
};

export default LocationCodeRackGoodsReceiveReturn;
